import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { pdvApi, importApi } from '../lib/api';
import { lookupCity, searchCities } from '../lib/comuni';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { toast } from 'sonner';
import {
  Store,
  Plus,
  Search,
  MapPin,
  Users,
  Phone,
  Mail,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Upload,
  Download,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const REGIONS = [
  'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
  'Friuli-Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
  'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
  'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto'
];

const PDVForm = ({ pdv, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(pdv || {
    name: '',
    company_name: '',
    azienda_id: '',
    vat_number: '',
    fiscal_code: '',
    pec: '',
    sdi_code: '',
    address: '',
    city: '',
    region: '',
    cap: '',
    province: '',
    // v102 — Sede Legale (può differire dalla sede operativa qui sopra)
    sede_legale_coincide: true,
    sede_legale_indirizzo: '',
    sede_legale_cap: '',
    sede_legale_citta: '',
    sede_legale_provincia: '',
    manager_name: '',
    manager_birth_place: '',
    manager_birth_date: '',
    phone: '',
    email: '',
    employee_count: '',
    area_sqm: '',
    notes: '',
    requires_haccp: false,
  });

  // Autocomplete città (da DB nazionale 7904 comuni)
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  // Autocomplete ragione sociale (da aziende/PDV già esistenti)
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyFiltered, setCompanyFiltered] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);

  // Carica aziende + pdv esistenti per autocomplete
  useEffect(() => {
    const load = async () => {
      try {
        const [azRes, pdvRes] = await Promise.all([
          api.get('/aziende').catch(() => ({ data: [] })),
          api.get('/pdv').catch(() => ({ data: [] })),
        ]);
        // Merge aziende + unique company_name dai PDV
        const aziende = (azRes.data || []).map((a) => ({
          source: 'azienda',
          azienda_id: a.id,
          label: a.denominazione,
          company_name: a.denominazione,
          vat_number: a.partita_iva || '',
          fiscal_code: a.codice_fiscale || '',
          pec: a.pec || '',
          phone: a.telefono || '',
          address: a.sede_legale || '',
          city: a.citta || '',
          cap: a.cap || '',
          province: a.provincia || '',
        }));
        const seenNames = new Set(aziende.map((a) => (a.company_name || '').toLowerCase()));
        const fromPdv = [];
        for (const p of (pdvRes.data || [])) {
          const cn = (p.company_name || '').trim();
          if (!cn) continue;
          const key = cn.toLowerCase();
          if (seenNames.has(key)) continue;
          seenNames.add(key);
          fromPdv.push({
            source: 'pdv',
            label: cn,
            company_name: cn,
            vat_number: p.vat_number || '',
            fiscal_code: p.fiscal_code || '',
            pec: p.pec || '',
            phone: p.phone || '',
            address: p.address || '',
            city: p.city || '',
            cap: p.cap || '',
            province: p.province || '',
          });
        }
        setCompanyOptions([...aziende, ...fromPdv]);
      } catch {
        setCompanyOptions([]);
      }
    };
    load();
  }, []);

  // Handle typing in city field - autocomplete
  const handleCityChange = async (value) => {
    setFormData((prev) => ({ ...prev, city: value }));
    if (value.trim().length >= 2) {
      const results = await searchCities(value, 8);
      setCitySuggestions(results);
      setShowCitySuggestions(results.length > 0);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  // Quando utente seleziona una città dall'autocomplete
  const handleCitySelect = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      city: suggestion.nome,
      region: suggestion.regione,
      cap: prev.cap || suggestion.cap || '',
      province: suggestion.provincia,
    }));
    setCitySuggestions([]);
    setShowCitySuggestions(false);
  };

  // Quando utente esce dal campo città senza selezionare: lookup esatto
  const handleCityBlur = async () => {
    setTimeout(() => setShowCitySuggestions(false), 200);
    if (formData.city && !formData.region) {
      const found = await lookupCity(formData.city);
      if (found) {
        setFormData((prev) => ({
          ...prev,
          region: found.regione,
          cap: prev.cap || found.cap,
          province: found.provincia,
        }));
      }
    }
  };

  // Handle typing in company_name - autocomplete da aziende esistenti
  const handleCompanyChange = (value) => {
    setFormData((prev) => ({ ...prev, company_name: value }));
    if (value.trim().length >= 2 && companyOptions.length > 0) {
      const q = value.toLowerCase();
      const matches = companyOptions
        .filter((o) => o.label.toLowerCase().includes(q))
        .slice(0, 6);
      setCompanyFiltered(matches);
      setShowCompanySuggestions(matches.length > 0);
    } else {
      setCompanyFiltered([]);
      setShowCompanySuggestions(false);
    }
  };

  // Quando utente seleziona un'azienda dall'autocomplete: autocompila i campi vuoti
  const handleCompanySelect = async (opt) => {
    setFormData((prev) => {
      // Autocompila solo campi vuoti (non sovrascrive)
      const next = {
        ...prev,
        company_name: opt.company_name,
        // Auto-link al record Azienda quando la selezione viene da una vera azienda
        azienda_id: opt.source === 'azienda' && opt.azienda_id ? opt.azienda_id : prev.azienda_id,
        vat_number: prev.vat_number || opt.vat_number,
        fiscal_code: prev.fiscal_code || opt.fiscal_code,
        pec: prev.pec || opt.pec,
        phone: prev.phone || opt.phone,
        address: prev.address || opt.address,
        city: prev.city || opt.city,
        cap: prev.cap || opt.cap,
        province: prev.province || opt.province,
      };
      return next;
    });
    // Dopo, completa regione se c'è la città
    if (opt.city) {
      const found = await lookupCity(opt.city);
      if (found) {
        setFormData((prev) => ({
          ...prev,
          region: prev.region || found.regione,
          province: prev.province || found.provincia,
          cap: prev.cap || found.cap,
        }));
      }
    }
    setCompanyFiltered([]);
    setShowCompanySuggestions(false);
    toast.success(`Dati di "${opt.company_name}" importati`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="name">Nome Unità Locale *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Supermercato Centro"
            required
            data-testid="pdv-name-input"
          />
        </div>
        <div className="col-span-2 space-y-2 relative">
          <Label htmlFor="company_name">Ragione Sociale</Label>
          <Input
            id="company_name"
            value={formData.company_name}
            onChange={(e) => handleCompanyChange(e.target.value)}
            onFocus={() => formData.company_name && formData.company_name.length >= 2 && setShowCompanySuggestions(companyFiltered.length > 0)}
            onBlur={() => setTimeout(() => setShowCompanySuggestions(false), 200)}
            placeholder="Inizia a scrivere per cercare tra le aziende esistenti..."
            autoComplete="off"
            data-testid="pdv-company-input"
          />
          {showCompanySuggestions && companyFiltered.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border border-input rounded-sm shadow-lg max-h-60 overflow-y-auto">
              {companyFiltered.map((opt, i) => (
                <button
                  key={`${opt.source}-${i}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleCompanySelect(opt)}
                  className="w-full text-left px-3 py-2 hover:bg-accent text-sm border-b border-border last:border-0 flex items-center justify-between gap-2"
                  data-testid={`company-suggestion-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{opt.label}</div>
                    {(opt.vat_number || opt.city) && (
                      <div className="text-xs text-muted-foreground truncate">
                        {opt.vat_number && `P.IVA ${opt.vat_number}`}
                        {opt.vat_number && opt.city && ' · '}
                        {opt.city && `${opt.city}${opt.province ? ` (${opt.province})` : ''}`}
                      </div>
                    )}
                  </div>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
                    {opt.source === 'azienda' ? 'Azienda' : 'UL'}
                  </span>
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Suggerimenti automatici da Aziende e Unità Locali già inserite
          </p>
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="azienda_id">Azienda di appartenenza *</Label>
          <select
            id="azienda_id"
            value={formData.azienda_id || ''}
            onChange={(e) => setFormData({ ...formData, azienda_id: e.target.value })}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="pdv-azienda-select"
          >
            <option value="">— Seleziona un'azienda —</option>
            {companyOptions
              .filter((o) => o.source === 'azienda' && o.azienda_id)
              .map((o) => (
                <option key={o.azienda_id} value={o.azienda_id}>
                  {o.label}{o.vat_number ? ` (P.IVA ${o.vat_number})` : ''}
                </option>
              ))}
          </select>
          <p className="text-xs text-muted-foreground">
            La UL sarà visibile a tutti gli amministratori di questa azienda.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="vat_number">Partita IVA</Label>
          <Input
            id="vat_number"
            value={formData.vat_number}
            onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
            placeholder="IT01234567890"
            data-testid="pdv-vat-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fiscal_code">Codice Fiscale</Label>
          <Input
            id="fiscal_code"
            value={formData.fiscal_code}
            onChange={(e) => setFormData({ ...formData, fiscal_code: e.target.value })}
            placeholder="01234567890"
            data-testid="pdv-fiscal-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pec">PEC</Label>
          <Input
            id="pec"
            type="email"
            value={formData.pec}
            onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
            placeholder="azienda@pec.it"
            data-testid="pdv-pec-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sdi_code">Codice SDI</Label>
          <Input
            id="sdi_code"
            value={formData.sdi_code}
            onChange={(e) => setFormData({ ...formData, sdi_code: e.target.value })}
            placeholder="ABC1234"
            maxLength={7}
            data-testid="pdv-sdi-input"
          />
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="address">Indirizzo *</Label>
          <Input
            id="address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="Via Roma, 123"
            required
            data-testid="pdv-address-input"
          />
        </div>
        <div className="col-span-2 space-y-2 relative">
          <Label htmlFor="city">Città *</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => handleCityChange(e.target.value)}
            onFocus={() => formData.city && formData.city.length >= 2 && setShowCitySuggestions(citySuggestions.length > 0)}
            onBlur={handleCityBlur}
            placeholder="Inizia a scrivere il nome del comune..."
            autoComplete="off"
            required
            data-testid="pdv-city-input"
          />
          {showCitySuggestions && citySuggestions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border border-input rounded-sm shadow-lg max-h-60 overflow-y-auto">
              {citySuggestions.map((s, i) => (
                <button
                  key={`${s.nome}-${s.provincia}-${i}`}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleCitySelect(s)}
                  className="w-full text-left px-3 py-2 hover:bg-accent text-sm border-b border-border last:border-0"
                  data-testid={`city-suggestion-${i}`}
                >
                  <div className="font-medium">{s.nome}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.provincia} · {s.regione} · CAP {s.cap}
                  </div>
                </button>
              ))}
            </div>
          )}
          {formData.region && !showCitySuggestions && (
            <p className="text-xs text-emerald-600">
              ✓ {formData.city} · {formData.province || '—'} · {formData.region}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="cap">CAP</Label>
          <Input
            id="cap"
            value={formData.cap || ''}
            onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
            placeholder="00100"
            maxLength={5}
            data-testid="pdv-cap-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="province">Provincia</Label>
          <Input
            id="province"
            value={formData.province || ''}
            onChange={(e) => setFormData({ ...formData, province: e.target.value.toUpperCase() })}
            placeholder="MI"
            maxLength={2}
            data-testid="pdv-province-input"
          />
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="region">Regione *</Label>
          <select
            id="region"
            value={formData.region}
            onChange={(e) => setFormData({ ...formData, region: e.target.value })}
            className="w-full h-10 px-3 rounded-sm border border-input bg-background text-sm"
            required
            data-testid="pdv-region-select"
          >
            <option value="">Seleziona regione</option>
            {REGIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* v102 — Sede Legale */}
        <div className="col-span-2 mt-2 p-3 border border-amber-300 bg-amber-50/50 rounded">
          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              id="sede_legale_coincide"
              checked={formData.sede_legale_coincide !== false}
              onChange={(e) => setFormData({ ...formData, sede_legale_coincide: e.target.checked })}
              className="w-4 h-4"
              data-testid="sede-coincide-checkbox"
            />
            <Label htmlFor="sede_legale_coincide" className="text-sm cursor-pointer font-medium">
              La <strong>sede legale</strong> coincide con la sede operativa qui sopra
            </Label>
          </div>
          {!formData.sede_legale_coincide && (
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid="sede-legale-fields">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Sede Legale — Indirizzo</Label>
                <Input value={formData.sede_legale_indirizzo || ''} onChange={(e) => setFormData({ ...formData, sede_legale_indirizzo: e.target.value })} placeholder="Via Roma 1" data-testid="sede-legale-indirizzo" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CAP</Label>
                <Input value={formData.sede_legale_cap || ''} onChange={(e) => setFormData({ ...formData, sede_legale_cap: e.target.value })} maxLength={5} placeholder="00100" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Città</Label>
                <Input value={formData.sede_legale_citta || ''} onChange={(e) => setFormData({ ...formData, sede_legale_citta: e.target.value })} placeholder="Roma" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Provincia</Label>
                <Input value={formData.sede_legale_provincia || ''} onChange={(e) => setFormData({ ...formData, sede_legale_provincia: e.target.value.toUpperCase() })} maxLength={2} placeholder="RM" />
              </div>
              <p className="col-span-2 text-[11px] text-amber-800 italic">Questi campi verranno utilizzati nei documenti dove appaiono i placeholder <code className="bg-white px-1 rounded">Sede_Legale_Indirizzo</code>, <code className="bg-white px-1 rounded">Sede_Legale_CAP</code>, <code className="bg-white px-1 rounded">Sede_Legale_Citta</code>, <code className="bg-white px-1 rounded">Sede_Legale_Provincia</code>, <code className="bg-white px-1 rounded">Sede_Legale_Completa</code>.</p>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="manager_name">Legale Rappresentante / Responsabile</Label>
          <Input
            id="manager_name"
            value={formData.manager_name}
            onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
            placeholder="Mario Rossi"
            data-testid="pdv-manager-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manager_birth_place">Luogo di nascita LR</Label>
          <Input
            id="manager_birth_place"
            value={formData.manager_birth_place || ''}
            onChange={(e) => setFormData({ ...formData, manager_birth_place: e.target.value })}
            placeholder="Roma"
            data-testid="pdv-manager-birth-place-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manager_birth_date">Data di nascita LR</Label>
          <Input
            id="manager_birth_date"
            value={formData.manager_birth_date || ''}
            onChange={(e) => setFormData({ ...formData, manager_birth_date: e.target.value })}
            placeholder="01/01/1970"
            data-testid="pdv-manager-birth-date-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Telefono</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+39 02 1234567"
            data-testid="pdv-phone-input"
          />
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="email">Email <span className="text-xs text-blue-700">(diventerà anche login utente Responsabile UL)</span></Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="pdv@esempio.it"
            data-testid="pdv-email-input"
          />
          {!pdv && formData.email && (
            <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
              ℹ️ Verrà creato automaticamente in <strong>Amministrazione → Utenti</strong> un account
              <strong> {formData.email}</strong> con ruolo <strong>Responsabile UL</strong> e password iniziale
              <code className="mx-1 bg-white px-1 rounded border border-blue-300">Password</code>
              (l&apos;utente dovrà cambiarla al primo accesso).
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="employee_count">N. Dipendenti</Label>
          <Input
            id="employee_count"
            type="number"
            value={formData.employee_count ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setFormData({ ...formData, employee_count: v === '' ? 0 : parseInt(v, 10) });
            }}
            min="0"
            placeholder="Inserisci numero"
            data-testid="pdv-employees-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="area_sqm">Superficie (mq)</Label>
          <Input
            id="area_sqm"
            type="number"
            value={formData.area_sqm ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setFormData({ ...formData, area_sqm: v === '' ? null : parseFloat(v) });
            }}
            min="0"
            data-testid="pdv-area-input"
          />
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="notes">Note</Label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Note aggiuntive..."
            className="w-full min-h-[80px] px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
            data-testid="pdv-notes-input"
          />
        </div>
        <div className="col-span-2 p-3 rounded-md border-2 border-amber-200 bg-amber-50">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!formData.requires_haccp}
              onChange={(e) => setFormData({ ...formData, requires_haccp: e.target.checked })}
              className="w-4 h-4 mt-0.5"
              data-testid="pdv-requires-haccp"
            />
            <div className="text-sm">
              <div className="font-semibold text-amber-900">
                Questa UL richiede l'autocontrollo HACCP
              </div>
              <div className="text-xs text-amber-800">
                Spunta se la UL è alimentare (ristorante, bar, supermercato, mensa,
                ecc.). Il datore di lavoro (utente "azienda") vedrà i moduli HACCP
                solo per le UL con questa opzione attiva.
              </div>
            </div>
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" data-testid="pdv-submit-btn">
          {pdv ? 'Salva Modifiche' : 'Crea Unità Locale'}
        </Button>
      </div>
    </form>
  );
};

const PDVCard = ({ pdv, onEdit, onDelete, onSelect, isSelected, canEdit = true }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    layout
  >
    <Card className={`hover-card h-full ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`} data-testid={`pdv-card-${pdv.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
              <Store className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                {pdv.name}
                {pdv.requires_haccp && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded font-medium" title="UL con autocontrollo HACCP">
                    HACCP
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="w-3 h-3" />
                {pdv.city}, {pdv.region}
              </div>
            </div>
          </div>
          {canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`pdv-menu-${pdv.id}`}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/pdv/${pdv.id}`} className="flex items-center">
                  <Eye className="w-4 h-4 mr-2" />
                  Visualizza
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(pdv)}>
                <Pencil className="w-4 h-4 mr-2" />
                Modifica
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(pdv)} className="text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          ) : (
            <Link to={`/pdv/${pdv.id}`}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Eye className="w-4 h-4" />
              </Button>
            </Link>
          )}
        </div>

        <p className="text-sm text-muted-foreground mb-4 line-clamp-1">{pdv.address}</p>

        <div className="grid grid-cols-2 gap-3 text-sm">
          {pdv.employee_count > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>{pdv.employee_count} dipendenti</span>
            </div>
          )}
          {pdv.phone && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="w-4 h-4" />
              <span className="truncate">{pdv.phone}</span>
            </div>
          )}
          {pdv.email && (
            <div className="flex items-center gap-2 text-muted-foreground col-span-2">
              <Mail className="w-4 h-4" />
              <span className="truncate">{pdv.email}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <Badge variant={pdv.status === 'active' ? 'default' : 'secondary'}>
            {pdv.status === 'active' ? 'Attivo' : 'Inattivo'}
          </Badge>
          <div className="flex items-center gap-2">
            <Button
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              className="text-xs"
              onClick={() => onSelect(pdv)}
              data-testid={`pdv-select-${pdv.id}`}
            >
              {isSelected ? '✓ Selezionata' : 'Seleziona'}
            </Button>
            <Link to={`/pdv/${pdv.id}`}>
              <Button variant="ghost" size="sm" className="text-xs" data-testid={`pdv-view-${pdv.id}`}>
                Dettagli →
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export default function PDVListPage() {
  const { user } = useAuth();
  const { currentPdvId, setCurrentPdvId } = useCurrentPdv();
  const handleSelectPdv = (pdv) => {
    if (currentPdvId === pdv.id) {
      setCurrentPdvId(null);
      toast.info('Selezione UL rimossa');
    } else {
      setCurrentPdvId(pdv.id);
      toast.success(`UL selezionata: ${pdv.name}`);
    }
  };
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Il verificatore non può creare/modificare/eliminare UL
  const isVerificatore = user?.role === 'verificatore';
  const canEdit = !isVerificatore;
  const [editingPdv, setEditingPdv] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pdvToDelete, setPdvToDelete] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchPdvs = async () => {
    try {
      const response = await pdvApi.getAll();
      setPdvs(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle Unità Locali');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPdvs();
  }, []);

  const handleSubmit = async (data) => {
    try {
      // Normalizza i campi numerici (stringhe vuote -> null/0)
      const payload = {
        ...data,
        employee_count: data.employee_count === '' || data.employee_count == null
          ? 0
          : Number(data.employee_count),
        area_sqm: data.area_sqm === '' || data.area_sqm == null || Number.isNaN(data.area_sqm)
          ? null
          : Number(data.area_sqm),
      };
      // v1.3.12 — Auto-creazione utente "Responsabile UL" dai dati del form:
      // se l'utente ha indicato manager_name + email, il backend crea automaticamente
      // un account con role=responsabile_ul, password predefinita "Password" e
      // must_change_password=true (forza cambio al primo accesso).
      if (!editingPdv && data.email && data.email.trim()) {
        const managerFull = (data.manager_name || '').trim();
        const [refNome, ...rest] = managerFull.split(' ');
        const refCognome = rest.join(' ');
        payload.auto_create_user = true;
        payload.referente_email = data.email.trim();
        payload.referente_nome = refNome || '';
        payload.referente_cognome = refCognome || '';
        payload.referente_phone = data.phone || '';
      }
      if (editingPdv) {
        await pdvApi.update(editingPdv.id, payload);
        toast.success('Unità locale aggiornata');
      } else {
        const created = await pdvApi.create(payload);
        const autoUser = created?.data?.auto_user_created;
        if (autoUser) {
          if (autoUser.existing) {
            toast.success(`Unità locale creata. Utente esistente "${autoUser.email}" assegnato come Responsabile UL.`);
          } else {
            toast.success(
              `Unità locale creata. Creato utente "${autoUser.email}" — password iniziale: "Password" (verrà richiesto il cambio al primo accesso).`,
              { duration: 8000 }
            );
          }
        } else {
          toast.success('Unità locale creata');
        }
      }
      setDialogOpen(false);
      setEditingPdv(null);
      fetchPdvs();
    } catch (error) {
      // Estrai messaggio d'errore in modo sicuro (evita crash su detail=array)
      const detail = error.response?.data?.detail;
      let msg = 'Errore durante il salvataggio';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((d) => (d?.msg ? `${(d.loc || []).join('.')}: ${d.msg}` : String(d))).join(' · ');
      } else if (detail && typeof detail === 'object') {
        msg = JSON.stringify(detail);
      }
      toast.error(msg);
    }
  };

  const handleEdit = (pdv) => {
    setEditingPdv(pdv);
    setDialogOpen(true);
  };

  const handleDeleteClick = (pdv) => {
    setPdvToDelete(pdv);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pdvToDelete) return;
    try {
      await pdvApi.delete(pdvToDelete.id);
      toast.success('Unità locale eliminata');
      setDeleteDialogOpen(false);
      setPdvToDelete(null);
      fetchPdvs();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const response = await importApi.importPdvExcel(file);
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`Importate ${imported} Unità Locali`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} righe saltate (duplicati o dati mancanti)`);
      }
      if (errors && errors.length > 0) {
        toast.warning(`Alcuni errori: ${errors[0]}`);
      }
      
      setImportDialogOpen(false);
      fetchPdvs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore importazione');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await importApi.downloadTemplate('pdv');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_pdv.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download template');
    }
  };

  const filteredPdvs = pdvs.filter((pdv) =>
    pdv.name.toLowerCase().includes(search.toLowerCase()) ||
    pdv.city.toLowerCase().includes(search.toLowerCase()) ||
    pdv.region.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="pdv-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="page-header mb-0">
          <h1 className="page-title">Unità Locali</h1>
          <p className="page-subtitle">{pdvs.length} unità locali registrate</p>
        </div>
        {canEdit && (
        <div className="flex gap-2">
          {/* Import Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-pdv-btn">
                <Upload className="w-4 h-4 mr-2" />
                Importa Excel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importa Struttura Distributiva</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Carica un file Excel con i dati delle unità locali. Il file deve contenere colonne come: Nome, Indirizzo, Città, Regione, CAP, Provincia, etc.
                </p>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleDownloadTemplate} className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    Scarica Template
                  </Button>
                </div>

                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportExcel}
                    className="hidden"
                    id="pdv-file-input"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                  >
                    {importing ? 'Importazione...' : 'Seleziona File Excel'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Formati supportati: .xlsx, .xls
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add PDV Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditingPdv(null);
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-pdv-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Unità Locale
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingPdv ? 'Modifica Unità Locale' : 'Nuova Unità Locale'}
                </DialogTitle>
              </DialogHeader>
              <PDVForm
                pdv={editingPdv}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setDialogOpen(false);
                  setEditingPdv(null);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Cerca per nome, città o regione..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="pdv-search-input"
        />
      </div>

      {/* Grid */}
      {filteredPdvs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPdvs.map((pdv) => (
            <PDVCard
              key={pdv.id}
              pdv={pdv}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onSelect={handleSelectPdv}
              isSelected={currentPdvId === pdv.id}
              canEdit={canEdit}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Store className="empty-state-icon" />
          <p className="empty-state-title">
            {search ? 'Nessun risultato' : 'Nessuna unità locale'}
          </p>
          <p className="empty-state-description">
            {search
              ? 'Prova a modificare i criteri di ricerca'
              : 'Nessuna unità locale disponibile'
            }
          </p>
          {!search && canEdit && (
            <Button className="mt-4" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Unità Locale
            </Button>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{pdvToDelete?.name}"? 
              Verranno eliminati anche tutti i documenti, formazioni e controlli associati.
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
